<template>
    <main>
        <BarometerNavbar :nav="generalBarometer.nav" />
        <BarometerHomeHero />
        <BarometerHomeInfo />
        <BarometerHomePrices />
        <LazyBarometerHomeMap v-if="hasMap" />
        <BarometerHomeStats />
        <BarometerHomeBanner />
    </main>
</template>

<script setup lang="ts">
    import {provide, defineAsyncComponent} from 'vue';
    import {createError, useAsyncData, useHead, useRequestURL, useRoute, useSeoMeta, useWebsitePageView} from '#imports';
    import {useBarometerPage} from '~/composables/useBarometerPage';
    import {BAROMETER_HOME} from '~/keys';
    import BarometerNavbar from '../BarometerNavbar.vue';
    import BarometerHomeBanner from './BarometerHomeBanner.vue';
    import BarometerHomeHero from './BarometerHomeHero.vue';
    import BarometerHomeInfo from './BarometerHomeInfo.vue';
    import BarometerHomePrices from './BarometerHomePrices.vue';
    import BarometerHomeStats from './BarometerHomeStats.vue';

    const LazyBarometerHomeMap = defineAsyncComponent(() => import('./BarometerHomeMap.vue'));

    const route = useRoute();
    const {fetchGeneralBarometer} = useBarometerPage({urlPrefix: route.params.urlPrefix as string});

    const {data, error} = await useAsyncData('barometer-home', () => Promise.all([fetchGeneralBarometer()]), {deep: true});
    if (!data.value || error.value) {
        throw createError('Error fetching barometer home : ' + error?.value);
    }

    const [generalBarometer] = data.value || [];

    const hasMap = generalBarometer.areaSizeRange.length > 3;

    provide(BAROMETER_HOME, {
        generalBarometer,
    });

    // SEO
    useSeoMeta({title: generalBarometer.title, description: 'barometer.page.meta'});

    // Canonical
    const {origin, pathname} = useRequestURL();
    const canonicalUrl = `${origin}${pathname}`;
    useHead({link: [{rel: 'canonical', href: canonicalUrl}]});

    // Tracking
    useWebsitePageView('cms', 'barometers');
</script>
